import type { AudioLayer, MediaLayer } from '@/lib/layers/types';

// Sound effects the assistant places: audio layers carrying an sfxId, with
// their own cap and a limit on how many play at once (the editor Player
// mounts at most 5 shared audio tags, and the clip's own audio uses some).

export const SFX_CATEGORIES = [
  'whoosh', 'impact', 'riser', 'pop', 'click', 'ding', 'fail',
  'glitch', 'cash', 'reaction', 'cartoon', 'typing', 'drama',
] as const;
export type SfxCategory = (typeof SFX_CATEGORIES)[number];

export const SFX_ALIGNS = ['start', 'peak'] as const;
export type SfxAlign = (typeof SFX_ALIGNS)[number];

export const MAX_SFX_LAYERS = 30;
export const MAX_CONCURRENT_SFX = 2;
export const DEFAULT_SFX_VOLUME = 0.6;
const MIN_SECONDS = 1 / 30;

export const isSfxLayer = (layer: MediaLayer): layer is AudioLayer & { sfxId: string } => (
  layer.type === 'audio' && typeof layer.sfxId === 'string' && layer.sfxId.length > 0
);

export class SfxPlacementError extends Error {
  constructor(readonly code: string, readonly detail?: string) {
    super(code);
  }
}

const ms = (value: number) => Math.round(value * 1000) / 1000;

/** Where a sound effect goes on the clip timeline. With align "peak", `at` is
 *  where its hit lands; a hit earlier than the sound's head trims the head. */
export function placeSoundEffect({ at, align, peakAt, duration, clipDuration, end }: {
  at: number;
  align: SfxAlign;
  peakAt: number;
  duration: number;
  clipDuration: number;
  end?: number;
}): { start: number; end: number; trimStart: number; trimEnd: number } {
  if (!Number.isFinite(at) || at < 0) throw new SfxPlacementError('invalid_time');
  if (at >= clipDuration) {
    throw new SfxPlacementError('outside_clip', `The clip is ${Math.round(clipDuration * 10) / 10} s long`);
  }
  const offset = align === 'peak' ? Math.min(Math.max(0, peakAt), duration) : 0;
  const rawStart = at - offset;
  const trimStart = rawStart < 0 ? -rawStart : 0;
  const start = Math.max(0, rawStart);
  let stop = Math.min(start + (duration - trimStart), clipDuration);
  if (end !== undefined && Number.isFinite(end)) stop = Math.min(stop, end);
  if (stop - start < MIN_SECONDS) throw new SfxPlacementError('outside_clip');
  return { start: ms(start), end: ms(stop), trimStart: ms(trimStart), trimEnd: ms(trimStart + (stop - start)) };
}

/** Most sound effects playing at one moment inside `window`. Back-to-back
 *  sounds do not overlap; audio without an sfxId does not count. */
export function peakSfxConcurrency(layers: readonly MediaLayer[], window: { start: number; end: number }): number {
  const events = layers
    .filter(isSfxLayer)
    .map((layer) => ({ start: Math.max(layer.start, window.start), end: Math.min(layer.end, window.end) }))
    .filter((span) => span.end - span.start > 1e-6)
    .flatMap((span) => [{ at: span.start, delta: 1 }, { at: span.end, delta: -1 }])
    .sort((a, b) => a.at - b.at || a.delta - b.delta);
  let current = 0;
  let peak = 0;
  events.forEach((event) => {
    current += event.delta;
    peak = Math.max(peak, current);
  });
  return peak;
}
